import { useEffect, useState } from 'react';
import { hasSignedInHint, v2 } from '../account/api';
import { v2Session } from '../../components/lists/lists';

/**
 * The browser half of following (prototype UserProfile Follow): who the viewer is and
 * whether they follow this diner, asked after the page loads so the server render stays
 * public, and the follow / unfollow calls.
 */

export const VIEWER = `query Viewer { viewer { username } }`;
export const VIEWER_FOLLOWING = `query ViewerFollowing($username: String!) {
    viewer { username }
    isFollowing(username: $username)
}`;
export const FOLLOW = `mutation FollowUser($username: String!) { followUser(username: $username) }`;
export const UNFOLLOW = `mutation UnfollowUser($username: String!) { unfollowUser(username: $username) }`;

export interface ViewerState {
    /** Null until known, or when signed out. */
    me: string | null;
    isMe: boolean;
    isFollowing: boolean;
}

const SIGNED_OUT: ViewerState = { me: null, isMe: false, isFollowing: false };

export const sameUser = (a: string | null | undefined, b: string) =>
    Boolean(a) && a!.toLowerCase() === b.toLowerCase();

export function useViewer(username: string): ViewerState {
    const [state, setState] = useState<ViewerState>(SIGNED_OUT);
    useEffect(() => {
        if (!hasSignedInHint()) return;
        let cancelled = false;
        const load = process.env.ENABLE_FOLLOWS
            ? v2<{
                  viewer: { username: string } | null;
                  isFollowing: boolean;
              }>(VIEWER_FOLLOWING, { username })
            : v2<{ viewer: { username: string } | null }>(VIEWER).then(d => ({
                  ...d,
                  isFollowing: false,
              }));
        load.then(
            d => {
                if (cancelled || !d.viewer) return;
                setState({
                    me: d.viewer.username,
                    isMe: sameUser(d.viewer.username, username),
                    isFollowing: d.isFollowing,
                });
            },
            () => undefined,
        );
        return () => {
            cancelled = true;
        };
    }, [username]);
    return state;
}

/** Follows or unfollows; throws SignedOutError when the session has gone. */
export const setFollowing = (username: string, follow: boolean) =>
    v2Session(follow ? FOLLOW : UNFOLLOW, { username });

/** "1 follower" / "12 followers". */
export const countLabel = (n: number, one: string, many: string) =>
    `${n.toLocaleString('en-AU')} ${n === 1 ? one : many}`;
